import type { ReactNode } from 'react';
import { useMemo } from 'react';
import type { TableDisplayState } from '../types';

/**
 * DataTableの表示状態を管理するカスタムフック
 * - データの状態に基づく適切なDisplayStateの決定
 * - パフォーマンス最適化（useMemoによる再計算防止）
 * - 型安全性の保証
 * @template TData データ配列の要素型
 * @param isPending TanStack Queryのquery.isPendingを使用
 * @param data 表示対象のデータ配列（空配列判定に使用）
 * @param options 表示オプション
 * @param options.isInitialState 初期状態かどうか（検索前、フィルター未適用など）
 * @param options.emptyContent データが空の場合に表示するカスタムコンテンツ
 * @param options.placeholderContent プレースホルダー状態で表示するカスタムコンテンツ
 * @returns メモ化されたTableDisplayState オブジェクト
 *
 * ## 使用例
 * ```typescript
 * const ProductPermissionsPage = () => {
 *   const { data, isPending } = useGetProductPermissions();
 *   const displayState = useTableDisplayState(
 *     isPending,
 *     data?.items ?? [],
 *     {
 *       isInitialState: !data, // データが一度も取得されていない場合
 *       emptyContent: <CustomEmptyMessage />,
 *       placeholderContent: <SearchPromptMessage />
 *     }
 *   );
 *
 *   return <DataTable table={table} displayState={displayState} />;
 * };
 *
 * ## 状態の優先順位
 * 1. `loading`: isPendingがtrueの場合（最優先）
 * 2. `placeholder`: isInitialStateがtrueかつisPendingがfalseの場合
 * 3. `empty`: データが空配列かつ初期状態ではない場合
 * 4. `data`: 通常のデータ表示状態（デフォルト）
 *
 * ## placeholder状態の使用想定
 * - 検索クエリが入力されていない初期状態
 * - フィルター条件が設定されていない状態
 * - データフェッチが一度も実行されていない状態
 * - ユーザーにアクションを促したい場合
 */
export const useTableDisplayState = <TData,>(isPending: boolean, data: TData[], options?: {
  /** 初期状態かどうか（検索前、フィルター未適用など） */
  isInitialState?: boolean;
  /** データが空の場合に表示するカスタムコンテンツ */
  emptyContent?: ReactNode;
  /** プレースホルダー状態で表示するカスタムコンテンツ */
  placeholderContent?: ReactNode;
}): TableDisplayState => {
  const isInitialState = options?.isInitialState;
  const emptyContent = options?.emptyContent;
  const placeholderContent = options?.placeholderContent;
  return useMemo(() => {
    // 1. 読み込み中の状態を最優先で処理
    if (isPending) {
      return {
        state: 'loading'
      } as const;
    }

    // 2. 初期状態（検索前、フィルター未適用など）の処理
    if (isInitialState) {
      return {
        state: 'placeholder',
        content: placeholderContent
      } as const;
    }

    // 3. データが空の場合の処理（初期状態ではない場合）
    if (data.length === 0) {
      return {
        state: 'empty',
        content: emptyContent
      } as const;
    }

    // 4. デフォルト：通常のデータ表示状態
    return {
      state: 'data'
    } as const;
  }, [isPending, data.length, isInitialState, emptyContent, placeholderContent]);
};